import { ApiService } from './ApiService';

export interface TranslationResult {
  translatedText: string;
  success: boolean;
  error?: string;
}

export interface BatchTranslationItem {
  key: string;
  text: string;
}

export interface BatchTranslationResult {
  translations: Record<string, string>;
  /** Keys whose text was already in the target language — no translation needed */
  sameLanguageKeys: string[];
  success: boolean;
  error?: string;
}

// Minimal in-memory cache for single translations: `${lang}:${text}` → translatedText
const translationCache = new Map<string, string>();

/**
 * Translates text to the specified target language via the backend API.
 */
export async function translate(
  text: string,
  targetLanguage: string,
): Promise<TranslationResult> {
  try {
    if (!text?.trim()) {
      return { translatedText: '', success: true };
    }

    const cacheKey = `${targetLanguage}:${text.trim()}`;
    if (translationCache.has(cacheKey)) {
      return { translatedText: translationCache.get(cacheKey)!, success: true };
    }

    const result = await translateBatch([{ key: '__single__', text }], targetLanguage);
    if (!result.success) {
      return { translatedText: '', success: false, error: result.error };
    }

    const translatedText = result.translations['__single__'] ?? '';
    translationCache.set(cacheKey, translatedText);
    return { translatedText, success: true };
  } catch (error) {
    console.error('Translation error:', error);
    return {
      translatedText: '',
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * Translates multiple text items via the backend API.
 */
export async function translateBatch(
  items: BatchTranslationItem[],
  targetLanguage: string,
): Promise<BatchTranslationResult> {
  try {
    const validItems = items.filter(item => item.text?.trim());
    if (validItems.length === 0) {
      return { translations: {}, sameLanguageKeys: [], success: true };
    }

    const api = ApiService.getInstance();
    const resp = await api.post<{
      translations: Record<string, string>;
      sameLanguageKeys: string[];
      success: boolean;
      error?: string;
    }>('/api/translate', { lang: targetLanguage, items: validItems });

    return {
      translations: resp.data.translations ?? {},
      sameLanguageKeys: resp.data.sameLanguageKeys ?? [],
      success: resp.data.success ?? true,
      error: resp.data.error,
    };
  } catch (error) {
    console.error('Batch translation error:', error);
    return {
      translations: {},
      sameLanguageKeys: [],
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

export default { translate, translateBatch };

// ---------------------------------------------------------------------------
// TranslationBatchManager
// Aggregates all translateBatch calls that happen for the same (orderId, lang)
// within a short debounce window into ONE API call, then persists the results
// to the backend and serves future requests from that persisted cache.
// ---------------------------------------------------------------------------

type PendingResolve = (translations: Record<string, string>) => void;

interface PendingItem {
  key: string;
  text: string;
  resolve: PendingResolve;
  retries: number; // how many empty-result retries have been attempted
}

const DEBOUNCE_MS = 80;
const MAX_EMPTY_RETRIES = 3;
/** Sentinel stored in the in-memory cache to mark "already in target language, skip forever" */
const SAME_LANG_SENTINEL = '__SAME_LANG__';

class TranslationBatchManagerImpl {
  // orderId+lang → already-loaded translations from backend
  private backendCache = new Map<string, Record<string, string>>();
  // orderId+lang → pending items waiting to be batched
  private pendingQueues = new Map<string, PendingItem[]>();
  // orderId+lang → debounce timer
  private timers = new Map<string, ReturnType<typeof setTimeout>>();
  // orderId+lang → in-flight backend load promise
  private loadPromises = new Map<string, Promise<Record<string, string>>>();

  private cacheKey(orderId: string, lang: string) {
    return `${orderId}::${lang}`;
  }

  /** Pre-warm the cache from the backend for an order. Call this when an order opens. */
  async preload(orderId: string, lang: string): Promise<void> {
    const key = this.cacheKey(orderId, lang);
    if (this.backendCache.has(key) || this.loadPromises.has(key)) return;
    await this.loadFromBackend(orderId, lang);
  }

  private async loadFromBackend(orderId: string, lang: string): Promise<Record<string, string>> {
    const key = this.cacheKey(orderId, lang);
    if (this.loadPromises.has(key)) return this.loadPromises.get(key)!;

    const promise = (async () => {
      try {
        const api = ApiService.getInstance();
        const resp = await api.get<{ translations: Record<string, string>; found: boolean }>(
          `/api/user/orders/${orderId}/translations?lang=${encodeURIComponent(lang)}`
        );
        const raw = resp.data.translations ?? {};
        // Strip out empty-string values persisted before the retry-on-empty fix,
        // but keep SAME_LANG_SENTINEL values so they remain skipped forever.
        const data: Record<string, string> = {};
        Object.entries(raw).forEach(([k, v]) => {
          if (v === SAME_LANG_SENTINEL || v !== '') data[k] = v;
        });
        this.backendCache.set(key, data);
        return data;
      } catch (err) {
        console.warn('[TranslationBatchManager] Failed to load from backend:', err);
        this.backendCache.set(key, {});
        return {};
      } finally {
        this.loadPromises.delete(key);
      }
    })();

    this.loadPromises.set(key, promise);
    return promise;
  }

  /**
   * Request translation for a batch of items belonging to a specific order.
   * Returns a map of key → translated text once the batch resolves.
   */
  async request(
    orderId: string,
    lang: string,
    items: BatchTranslationItem[],
  ): Promise<Record<string, string>> {
    if (!items.length) return {};

    // No orderId — fall back to direct translateBatch (no persistence)
    if (!orderId) {
      console.warn('[TranslationBatchManager] No orderId — falling back to direct translateBatch');
      const fallback = await translateBatch(items, lang);
      return fallback.translations;
    }

    const cacheKey = this.cacheKey(orderId, lang);

    // Ensure backend cache is loaded
    if (!this.backendCache.has(cacheKey)) {
      await this.loadFromBackend(orderId, lang);
    }

    const cached = this.backendCache.get(cacheKey) ?? {};

    // Split into already-cached and missing items
    const result: Record<string, string> = {};
    const missing: BatchTranslationItem[] = [];

    for (const item of items) {
      const textKey = item.text.trim();
      const cachedVal = cached[textKey];
      if (textKey in cached && cachedVal !== '' && cachedVal !== SAME_LANG_SENTINEL) {
        result[item.key] = cachedVal;
      } else if (cachedVal === SAME_LANG_SENTINEL) {
        result[item.key] = '';
      } else {
        missing.push(item);
      }
    }

    if (missing.length === 0) return result;

    // Queue missing items and wait for the debounced batch to resolve them
    const promises = missing.map(item =>
      new Promise<{ key: string; translation: string }>(resolve => {
        const queue = this.pendingQueues.get(cacheKey) ?? [];
        queue.push({
          key: item.key,
          text: item.text,
          retries: 0,
          resolve: (translations) => resolve({ key: item.key, translation: translations[item.key] ?? '' }),
        });
        this.pendingQueues.set(cacheKey, queue);
      })
    );

    this.scheduleBatch(orderId, lang);

    const resolved = await Promise.all(promises);
    for (const { key, translation } of resolved) {
      result[key] = translation;
    }
    return result;
  }

  private scheduleBatch(orderId: string, lang: string) {
    const cacheKey = this.cacheKey(orderId, lang);
    if (this.timers.has(cacheKey)) return; // already scheduled

    const timer = setTimeout(async () => {
      this.timers.delete(cacheKey);
      const queue = this.pendingQueues.get(cacheKey) ?? [];
      this.pendingQueues.delete(cacheKey);

      if (queue.length === 0) return;

      // Deduplicate by text so we don't translate the same string twice
      const seen = new Map<string, PendingItem[]>();
      for (const item of queue) {
        const t = item.text.trim();
        if (!seen.has(t)) seen.set(t, []);
        seen.get(t)!.push(item);
      }

      const uniqueItems: BatchTranslationItem[] = [];
      let idx = 0;
      const uniqueKeyToText: Record<string, string> = {};
      seen.forEach((_, text) => {
        const k = `__batch_${idx++}`;
        uniqueItems.push({ key: k, text });
        uniqueKeyToText[k] = text;
      });

      // Single aggregated backend translate call
      let batchResult: BatchTranslationResult;
      try {
        const api = ApiService.getInstance();
        const resp = await api.post<{
          translations: Record<string, string>;
          sameLanguageKeys: string[];
          success: boolean;
          error?: string;
        }>(`/api/user/orders/${orderId}/translations/translate`, {
          lang,
          items: uniqueItems,
        });
        batchResult = {
          translations: resp.data.translations ?? {},
          sameLanguageKeys: resp.data.sameLanguageKeys ?? [],
          success: resp.data.success ?? true,
          error: resp.data.error,
        };
      } catch (err) {
        console.warn('[TranslationBatchManager] Backend translate failed, falling back to direct:', err);
        batchResult = await translateBatch(uniqueItems, lang);
      }

      // Mark same-language texts in the in-memory cache with a sentinel so they
      // are never re-queued. Backend already persisted them.
      const sameLanguageTexts = new Set<string>(
        batchResult.sameLanguageKeys.map(k => uniqueKeyToText[k]).filter(Boolean)
      );
      sameLanguageTexts.forEach(text => {
        const key = this.cacheKey(orderId, lang);
        const cur = this.backendCache.get(key) ?? {};
        cur[text] = SAME_LANG_SENTINEL;
        this.backendCache.set(key, cur);
      });

      // Build text→translation map and update backend cache
      // Skip empty translations so they are retried on the next request.
      const newEntries: Record<string, string> = {};
      Object.entries(uniqueKeyToText).forEach(([uniqueKey, text]) => {
        if (sameLanguageTexts.has(text)) return; // handled above
        const translation = batchResult.translations[uniqueKey] ?? '';
        if (translation !== '') {
          newEntries[text] = translation;
        }
      });

      // Merge into in-memory backend cache
      const existing = this.backendCache.get(cacheKey) ?? {};
      this.backendCache.set(cacheKey, { ...existing, ...newEntries });

      // Backend endpoint already persisted — no extra POST needed here.

      // For items that came back empty, re-queue them (don't resolve yet) so the
      // same promise chain eventually gets a real value.  Items with a real
      // translation are resolved immediately.
      const retryQueue: PendingItem[] = [];
      seen.forEach((pendingItems, text) => {
        if (sameLanguageTexts.has(text)) {
          // Already in target language — resolve with empty, skip retries
          pendingItems.forEach(item => item.resolve({ [item.key]: '' }));
          return;
        }
        const translation = newEntries[text] ?? '';
        if (translation !== '') {
          pendingItems.forEach(item => item.resolve({ [item.key]: translation }));
        } else {
          pendingItems.forEach(item => {
            if (item.retries >= MAX_EMPTY_RETRIES) {
              console.warn(`[TranslationBatchManager] Giving up on "${text}" after ${MAX_EMPTY_RETRIES} retries`);
              item.resolve({ [item.key]: '' });
            } else {
              retryQueue.push({ ...item, retries: item.retries + 1 });
            }
          });
        }
      });

      if (retryQueue.length > 0) {
        console.warn(`[TranslationBatchManager] ${retryQueue.length} item(s) returned empty — retrying in 2s`);
        const existing2 = this.pendingQueues.get(cacheKey) ?? [];
        this.pendingQueues.set(cacheKey, [...existing2, ...retryQueue]);
        if (!this.timers.has(cacheKey)) {
          const retryTimer = setTimeout(() => {
            this.timers.delete(cacheKey);
            this.scheduleBatch(orderId, lang);
          }, 2000);
          this.timers.set(cacheKey, retryTimer);
        }
      }
    }, DEBOUNCE_MS);

    this.timers.set(cacheKey, timer);
  }
}

export const TranslationBatchManager = new TranslationBatchManagerImpl();


